import StairsOutlinedIcon from '@mui/icons-material/StairsOutlined';
import { BaseWidget } from "./BaseWidget";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import templatePersonaNotice from "./_shared/templatePersonaNotice";
import NO_PERSONA_TEXT from "./_shared/noPersonaText";
import usePersonaLadder from "./_shared/usePersonaLadder";
import usePublicPersona from "./_shared/usePublicPersona";
import { publicLadderState } from "../../../../helpers/liveReportHelpers/publicPersonaState";
import { LadderViz, StepChangePill, LadderEndResult } from "../../../personas/participation/LadderProgress";

// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md
// De participatieladder van de deelnemer van het rapport: begintrede, huidige
// trede en het verschil, met dezelfde ladder als het tabblad Participatieladder
// (LadderProgress). Geen bedragen: die horen bij het tabblad.
//
// Lezen en tonen zijn gescheiden: PersonaLadderView krijgt `status` + `steps`,
// uit usePersonaLadder in de app of uit `personaData.ladder` op de link. De
// server stuurt daar alleen de twee treden, niet het profiel erachter.

/**
 * @extends {BaseWidget}
 */
export class PersonaLadderWidget extends BaseWidget {

    getName() { return 'Participatieladder'; }
    getCategory() { return 'medewerkers'; }
    getRequiredModule() { return 'personas'; }
    getIcon() { return <StairsOutlinedIcon />; }

    getControls() {
        return {};
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * De ladder zonder ophalen.
 *
 * @param {object} props
 * @param {import('./_shared/usePersonaLadder').PersonaLadderStatus} props.status - Toestand van het ophalen
 * @param {import('../../../../helpers/participationHelpers/ladderSteps').LadderSteps|null} props.steps - Begin, nu en verschil
 * @returns {JSX.Element}
 */
export const PersonaLadderView = ({ status, steps }) => {
    if (status === 'idle') {
        return <p className="persona-ladder-message">{NO_PERSONA_TEXT}</p>;
    }
    if (status === 'error') {
        return <p role="alert" className="persona-ladder-message">De participatieladder kon niet worden geladen.</p>;
    }
    if (status === 'missing') {
        return <p className="persona-ladder-message">Deze deelnemer bestaat niet meer</p>;
    }
    // Zolang de lezing loopt niets beweren, ook niet 'nog niets ingevuld'.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    if (status !== 'ready' || !steps) {
        return <p className="persona-ladder-message">Participatieladder laden…</p>;
    }
    if (steps.begin === null && steps.current === null) {
        return <p className="persona-ladder-message">Nog geen participatieladder ingevuld voor deze deelnemer.</p>;
    }

    return (
        <div className="participation-ladder-progress persona-ladder-widget">
            <div className="persona-ladder-widget-head">
                <span className="persona-ladder-widget-subtitle">Beginsituatie → Huidige situatie</span>
                <StepChangePill change={steps.change} />
            </div>
            <LadderViz from={steps.begin} to={steps.current} />
            <LadderEndResult from={steps.begin} to={steps.current} />
        </div>
    );
};

/**
 * @returns {JSX.Element}
 */
const Renderer = () => {
    const { isPublic, personaData, personasModuleOn, personaId, templateStatus } = usePublicPersona();
    // Op de link en in een template leest de hook niets (`null`); de treden komen van de server.
    const live = usePersonaLadder(isPublic || !personasModuleOn ? null : personaId);
    const { status, steps } = isPublic && personaId ? publicLadderState(personaData) : live;

    const notice = templatePersonaNotice(templateStatus);
    if (notice) return notice;
    if (!personasModuleOn) {
        return <ModulePlaceholder mode="disabled" moduleLabel="Deelnemers" />;
    }
    if (personaId === null) {
        return <PersonaLadderView status="idle" steps={null} />;
    }
    return <PersonaLadderView status={status} steps={steps} />;
};

export default PersonaLadderWidget;
